import { Box, Typography, useTheme, Grid, List, ListItem, ListItemIcon } from '@mui/material';
import React from 'react';
import { FaBriefcase, FaStar, FaTrophy } from 'react-icons/fa';
import { FiberManualRecord as BulletPointIcon } from '@mui/icons-material';
import { ArrayField, SectionHeader } from '../Components';
import { SectionProps } from '../../../../types/ndisSection/carePlanInfoTypes';

const GoalsSection: React.FC<SectionProps> = ({
  editMode,
  isMobile,
  handleEditToggle,
  handleSubmit,
  handleCancel,
  control,
  errors,
  getValues,
  carePlan,
  shortTermGoals,
  setShortTermGoals,
  longTermGoals,
  setLongTermGoals
}) => {
  const theme = useTheme();
  const spacing = isMobile ? 1 : 2;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        mx: 'auto'
      }}
    >
      <SectionHeader
        icon={<FaStar size={isMobile ? 16 : 20} />}
        title="Goals"
        color={theme.palette.success.main}
        editMode={editMode}
        handleEditToggle={handleEditToggle}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        getValues={getValues}
      />

      <Box sx={{ p: isMobile ? 1 : 2 }}>
        {editMode ? (
          <>
            {shortTermGoals && setShortTermGoals && (
              <Box sx={{ mb: spacing }}>
                <ArrayField
                  name="Goals.ShortTerm"
                  label="Short Term Goals"
                  icon={<FaTrophy size={isMobile ? 14 : 16} />}
                  color={theme.palette.success.main}
                  items={shortTermGoals}
                  setItems={setShortTermGoals}
                  editMode={editMode}
                  errors={errors}
                />
              </Box>
            )}
            {longTermGoals && setLongTermGoals && (
              <Box sx={{ mb: spacing }}>
                <ArrayField
                  name="Goals.LongTerm"
                  label="Long Term Goals"
                  icon={<FaBriefcase size={isMobile ? 14 : 16} />}
                  color={theme.palette.success.main}
                  items={longTermGoals}
                  setItems={setLongTermGoals}
                  editMode={editMode}
                  errors={errors}
                />
              </Box>
            )}
          </>
        ) : (
          <Grid container spacing={spacing}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: isMobile ? 1 : 2,
                  backgroundColor: theme.palette.background.default,
                  borderRadius: 1,
                  height: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.success.light,
                      color: theme.palette.success.contrastText,
                      borderRadius: '50%',
                      width: isMobile ? 32 : 36,
                      height: isMobile ? 32 : 36,
                      flexShrink: 0
                    }}
                  >
                    <FaTrophy size={isMobile ? 14 : 16} />
                  </Box>
                  <Typography
                    variant={isMobile ? 'subtitle2' : 'h6'}
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.secondary
                    }}
                  >
                    Short Term Goals
                  </Typography>
                </Box>

                {shortTermGoals?.length > 0 ? (
                  <List dense sx={{ py: 0 }}>
                    {shortTermGoals.map((goal, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 1 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <BulletPointIcon
                            sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}
                          />
                        </ListItemIcon>
                        <Typography variant="body2">{goal}</Typography>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', pl: 4 }}>
                    No short term goals set
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: isMobile ? 1 : 2,
                  backgroundColor: theme.palette.background.default,
                  borderRadius: 1,
                  height: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.success.light,
                      color: theme.palette.success.contrastText,
                      borderRadius: '50%',
                      width: isMobile ? 32 : 36,
                      height: isMobile ? 32 : 36,
                      flexShrink: 0
                    }}
                  >
                    <FaBriefcase size={isMobile ? 14 : 16} />
                  </Box>
                  <Typography
                    variant={isMobile ? 'subtitle2' : 'h6'}
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.secondary
                    }}
                  >
                    Long Term Goals
                  </Typography>
                </Box>

                {longTermGoals?.length > 0 ? (
                  <List dense sx={{ py: 0 }}>
                    {longTermGoals.map((goal, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 1 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <BulletPointIcon
                            sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}
                          />
                        </ListItemIcon>
                        <Typography variant="body2">{goal}</Typography>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', pl: 4 }}>
                    No long term goals set
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default GoalsSection;
